'use client';

import {
  TableActionSlot,
  TableActionsSlot,
  TableActiveFiltersSlot,
  TableBodyCellSlot,
  TableBodySlot,
  TableBulkActionSlot,
  TableCaptionSlot,
  TableFilterChipsSlot,
  TableFootSlot,
  TableHeadCellSlot,
  TableHeadCellSortSlot,
  TableHeadSlot,
  TablePaginationInfoSlot,
  TablePaginationJumpToSlot,
  TablePaginationSlot,
  TableRowSlot,
  TableSearchInputSlot,
  TableSelectionBarSlot,
  TableSlot,
  TableSpinnerSlot,
  TableTextContext,
  TableTitleSlot,
  type tableVariants,
} from '@magicschool/spellbook/components/Table/slots';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { useMemo } from 'react';

export type TableProps = React.ComponentProps<'table'> &
  VariantProps<typeof tableVariants> & {
    /** When `true`, displays a loading spinner in place of the table content. */
    isLoading?: boolean;
    /** Configuration for component default text content, labels, placeholders, and screen reader announcements. */
    text?: {
      /** The accessible loading text for screen readers. Defaults to 'Loading...'. */
      loading?: string;
      /** Screen reader label for the dash in an empty cell. Defaults to 'No value'. */
      emptyCellLabel?: string;
    };
    /** Object containing refs to forward to internal elements. Use `refs.table` to get a ref to the underlying `<table>` element. */
    refs?: {
      table?: React.Ref<HTMLTableElement>;
    };
  };

export const tableWrapperVariants = cva('relative w-full flex overflow-auto max-h-fit border rounded-sm border-border-default', {
  variants: {
    fullWidth: {
      true: 'w-full',
      false: 'w-fit',
    },
  },
});

export const tableComponentVariants = cva('flex flex-col gap-2', {
  variants: {
    fullWidth: {
      true: 'w-full',
      false: 'w-fit',
    },
  },
});

export const tableHeadingWrapperVariants = cva('flex-col items-start gap-4 w-full mb-2', {
  variants: {
    withFilterChips: {
      true: '',
      false: '',
    },
  },
});

const TableBase: React.FC<TableProps> = ({ children, className, fullWidth = true, isLoading, text = {}, refs, ...props }) => {
  const searchInputId = useId();
  const tableId = useId();
  const titleId = useId();
  const cellText = useMemo(() => ({ emptyCellLabel: text.emptyCellLabel }), [text.emptyCellLabel]);

  const { slots } = useSlots(children, {
    table: { Component: TableSlot, props: { ...props, fullWidth, id: tableId, ref: refs?.table } },
    title: { Component: TableTitleSlot, props: { id: titleId } },
    searchInput: { Component: TableSearchInputSlot, props: { id: searchInputId, 'aria-controls': tableId } },
    filterChips: { Component: TableFilterChipsSlot, props: { id: searchInputId, 'aria-controls': tableId } },
    actions: { Component: TableActionsSlot },
    activeFilters: { Component: TableActiveFiltersSlot },
    selectionBar: { Component: TableSelectionBarSlot },
    paginationInfo: { Component: TablePaginationInfoSlot },
    pagination: { Component: TablePaginationSlot },
    paginationJumpTo: { Component: TablePaginationJumpToSlot },
    spinner: { Component: TableSpinnerSlot, props: { text } },
  });

  if (!slots.table) {
    console.warn('Table component requires a TableSlot child.');
    return null;
  }

  /**
   * @TODO [SPE-37] Utility for post-slot prop updates
   * https://linear.app/magicschool/issue/SPE-37/utility-for-post-slot-prop-updates
   */
  const titleSlot = slots.title;
  if (titleSlot && 'props' in titleSlot) {
    const titleProps = {
      ...(titleSlot.props ?? {}),
      id: titleSlot.props?.id ?? titleId,
    };
    slots.title = {
      ...titleSlot,
      props: titleProps,
    };
  }

  const tableSlot = slots.table;
  let tableProps;
  let titleIdForAria;
  if (slots.title && !Array.isArray(slots.title) && 'props' in slots.title) {
    titleIdForAria = slots.title.props?.id ?? titleId;
  }
  if (slots.title && 'props' in tableSlot) {
    tableProps = {
      ...(tableSlot.props ?? {}),
      'aria-labelledby': titleIdForAria,
      id: tableSlot.props?.id ?? tableId,
    };
    slots.table = {
      ...tableSlot,
      props: tableProps,
    };
  }

  const searchInputSlot = slots.searchInput;
  if (searchInputSlot && 'props' in searchInputSlot) {
    const searchInputProps = {
      ...(searchInputSlot.props ?? {}),
      id: searchInputSlot.props?.id ?? searchInputId,
      'aria-controls': !Array.isArray(slots.table) && 'props' in slots.table ? (slots.table.props?.id ?? tableId) : tableId,
    };
    slots.searchInput = {
      ...searchInputSlot,
      props: searchInputProps,
    };
  }

  if (slots.filterChips && 'props' in slots.filterChips) {
    const filterChipsProps = {
      ...(slots.filterChips.props ?? {}),
      id: slots.filterChips.props?.id ?? searchInputId,
      'aria-controls': !Array.isArray(slots.table) && 'props' in slots.table ? (slots.table.props?.id ?? tableId) : tableId,
    };
    slots.filterChips = {
      ...slots.filterChips,
      props: filterChipsProps,
    };
  }

  return (
    <div data-spellbook-name="table" className={tableComponentVariants({ fullWidth })}>
      {slots.title || slots.searchInput || slots.filterChips || slots.actions ? (
        <div
          className={tableHeadingWrapperVariants({ withFilterChips: Boolean(slots.filterChips) })}
          data-spellbook-name="table-heading-wrapper"
        >
          {slots.title && (
            <div data-spellbook-name="table-heading" className="flex items-center mb-3">
              {slots.title}
            </div>
          )}
          <div data-spellbook-name="table-search-filters-actions" className="w-full flex flex-wrap gap-4">
            {slots.searchInput}
            {slots.filterChips}
            {slots.actions}
          </div>
        </div>
      ) : null}
      {slots.activeFilters}
      {slots.selectionBar}

      <div data-spellbook-name="table-container" className={cn(tableWrapperVariants({ fullWidth }), className)}>
        <TableTextContext.Provider value={cellText}>{isLoading ? slots.spinner : slots.table}</TableTextContext.Provider>
      </div>

      {slots.paginationInfo || slots.pagination || slots.paginationJumpTo ? (
        <div data-spellbook-name="table-pagination" className="flex justify-between items-center gap-2 mt-3">
          {slots.paginationInfo}
          {slots.pagination}
          {slots.paginationJumpTo}
        </div>
      ) : null}
    </div>
  );
};

/**
 * Slot-based table component for structured, semantic data display with optional search, filtering, sorting, pagination, and actions.
 *
 * ### When to use
 * - Displaying structured tabular data with semantic HTML (`<table>`, `<thead>`, `<tbody>`, `<th>`, `<td>`)
 * - Tables that need built-in slots for title, search, filters, actions, and pagination
 * - Static or lightly interactive tables where you control rendering directly
 *
 * ### When NOT to use
 * - Large datasets needing built-in sorting, pagination, and column management: use `DataGrid` instead
 * - Non-tabular layouts (cards, lists): use appropriate layout components instead
 *
 * ### API
 * - `variant`: Visual style: `banded` (default) for alternating row backgrounds, `default` for plain rows
 * - `size`: Row spacing: `sm` for compact layouts, `md` (default) for standard spacing
 * - `fullWidth`: When `true` (default), the table fills its container width
 * - `wrapText`: When `true` (default), text wraps in cells. When `false`, text truncates with ellipsis.
 * - `visibleHover`: When `true` (default), rows highlight on hover
 * - `vAlignCells`: Vertical alignment of body cells: `top`, `middle` (default), or `bottom`. Only applies when `wrapText` is enabled.
 * - `isLoading`: When `true`, replaces the table with a loading spinner
 *
 * ### Composition (slots)
 * - `<Table.Table>`: (required) The `<table>` element itself. The outer `<Table>` is a `div` wrapper. Must always be included.
 * - `<Table.Title>`: Accessible table title. Renders as `heading-4` text. Automatically linked to the table via `aria-labelledby`. Only include when adding a visible title — not rendered by default.
 * - `<Table.Head>`: Table header (`<thead>`). Contains `<Table.Row>` with `<Table.HeadCell>` children.
 * - `<Table.Body>`: Table body (`<tbody>`). Contains `<Table.Row>` with `<Table.Cell>` children.
 * - `<Table.Foot>`: Table footer (`<tfoot>`). Only include for summary rows — not rendered by default.
 * - `<Table.Row>`: Table row (`<tr>`). Used in Head, Body, and Foot.
 * - `<Table.HeadCell>`: Header cell (`<th>`). Use `cellVariant` to control alignment: `text` (left), `number` (right), `component`, or `empty` (centered).
 * - `<Table.HeadCellSort>`: Sortable header button. Accepts `direction` (`asc`, `desc`, or `undefined`) and `onClick`. Provides accessible sort labels.
 * - `<Table.Cell>`: Body cell (`<td>`). Use `cellVariant` to control alignment (same options as `HeadCell`). Buttons inside a cell are `sm` with the `default` shape, neutral `ghost` by default, primary `muted` only for a row's call to action, and never bordered; the "Conventions/Buttons in tables" docs page is the full rule. An empty `text`/`number` cell shows an em dash in the border color automatically; render nothing rather than a placeholder like `'—'` or `'-'`. A `component` cell opts in with `emptyCell`; `text.emptyCellLabel` translates the dash's screen reader label.
 * - `<Table.Caption>`: Table caption (`<caption>`). Place as a child of `<Table.Table>`. Only include for additional context — not rendered by default.
 * - `<Table.SearchInput>`: Search field in the toolbar area. Auto-linked to the table via `aria-controls`. Only include when adding search — not rendered by default.
 * - `<Table.FilterChips>`: Filter chip container in the toolbar. Pass `FilterChipToggle` or `FilterChipSelect` as children. Only include when adding filters — not rendered by default.
 * - `<Table.Actions>`: Right-aligned action button container. Pass `<Table.Action>` children. Only include when adding table-level actions — not rendered by default.
 * - `<Table.Action>`: Individual action button within `<Table.Actions>`. Renders as `Button` with `size="sm"` and `variant="outlined"` by default.
 * - `<Table.ActiveFilters>`: Area for displaying currently applied filter chips. Only include when using filters — not rendered by default.
 * - `<Table.Pagination>`: Pagination controls container. Pass a `Pagination` component as child. Only include when paginating — not rendered by default.
 * - `<Table.PaginationInfo>`: Displays items-per-page and total count. Only include when paginating — not rendered by default.
 * - `<Table.PaginationJumpTo>`: Jump-to-page select. Only include when paginating — not rendered by default.
 * - `<Table.Spinner>`: Loading spinner shown when `isLoading` is `true`. Rendered automatically — no need to include explicitly.
 *
 * ### Accessibility
 * - When `<Table.Title>` is provided, the table is automatically labeled via `aria-labelledby`
 * - `<Table.SearchInput>` is automatically linked to the table via `aria-controls`
 * - Use `<Table.HeadCellSort>` for sortable columns — it provides accessible labels for sort direction
 * - Use `<Table.Caption>` for additional table context accessible to screen readers
 *
 * ### Component structure
 * ```tsx
 * <Table>
 *   <Table.Title />
 *   <Table.SearchInput />
 *   <Table.FilterChips />
 *   <Table.Actions>
 *     <Table.Action />
 *   </Table.Actions>
 *   <Table.ActiveFilters />
 *   <Table.Table>
 *     <Table.Caption />
 *     <Table.Head>
 *       <Table.Row>
 *         <Table.HeadCell>
 *           <Table.HeadCellSort />
 *         </Table.HeadCell>
 *       </Table.Row>
 *     </Table.Head>
 *     <Table.Body>
 *       <Table.Row>
 *         <Table.Cell />
 *       </Table.Row>
 *     </Table.Body>
 *     <Table.Foot>
 *       <Table.Row>
 *         <Table.Cell />
 *       </Table.Row>
 *     </Table.Foot>
 *   </Table.Table>
 *   <Table.PaginationInfo />
 *   <Table.Pagination />
 *   <Table.PaginationJumpTo />
 * </Table>
 * ```
 */
export const Table = Object.assign(TableBase, {
  Title: TableTitleSlot,
  Table: TableSlot,
  Head: TableHeadSlot,
  Body: TableBodySlot,
  Foot: TableFootSlot,
  Row: TableRowSlot,
  Caption: TableCaptionSlot,
  HeadCell: TableHeadCellSlot,
  HeadCellSort: TableHeadCellSortSlot,
  Cell: TableBodyCellSlot,
  SearchInput: TableSearchInputSlot,
  FilterChips: TableFilterChipsSlot,
  Actions: TableActionsSlot,
  Action: TableActionSlot,
  BulkAction: TableBulkActionSlot,
  ActiveFilters: TableActiveFiltersSlot,
  SelectionBar: TableSelectionBarSlot,
  PaginationInfo: TablePaginationInfoSlot,
  Pagination: TablePaginationSlot,
  PaginationJumpTo: TablePaginationJumpToSlot,
  Spinner: TableSpinnerSlot,
});
